import { SERVED_REGIONS, SERVED_REGION_CODES, SERVED_REGION_COUNTRIES, type ServedRegion, type ServedRegionCountry } from '@evi/shared';

/**
 * The region picker's rules (components/regions/RegionPicker.tsx), as plain
 * functions so they are tested without a browser. Every list they return is
 * in the shared list's order (US states, then Canadian provinces), the order
 * the Api stores.
 */

const ORDER = new Map(SERVED_REGION_CODES.map((c, i) => [c, i]));
const inOrder = (codes: Iterable<string>) => [...new Set(codes)].filter((c) => ORDER.has(c)).sort((a, b) => ORDER.get(a)! - ORDER.get(b)!);

export interface RegionGroup { country: ServedRegionCountry; label: string; regions: ServedRegion[] }

/** The regions grouped by country, those matching the search (name or code,
 *  any case). A country with no match is left out. */
export function regionGroups(query = ''): RegionGroup[] {
  const q = query.trim().toLowerCase();
  return SERVED_REGION_COUNTRIES.map((c) => ({
    country: c.country, label: c.label,
    regions: SERVED_REGIONS.filter((r) => r.country === c.country && (!q || r.name.toLowerCase().includes(q) || r.code.toLowerCase() === q)),
  })).filter((g) => g.regions.length);
}

export function toggleRegion(value: readonly string[], code: string): string[] {
  return value.includes(code) ? inOrder(value.filter((c) => c !== code)) : inOrder([...value, code]);
}

/** Add every code of a group (on) or remove them all (off). */
export function toggleRegionGroup(value: readonly string[], codes: readonly string[], on: boolean): string[] {
  const drop = new Set(codes);
  return on ? inOrder([...value, ...codes]) : inOrder(value.filter((c) => !drop.has(c)));
}

/** The picker button: the empty text, up to three codes, or a count. */
export function regionButtonLabel(value: readonly string[], emptyLabel: string): string {
  const codes = inOrder(value);
  if (!codes.length) return emptyLabel;
  if (codes.length <= 3) return codes.join(', ');
  return `${codes.length} regions`;
}

/** Same members, ignoring order. */
export function sameRegions(a: readonly string[] | null | undefined, b: readonly string[] | null | undefined): boolean {
  const x = inOrder(a ?? []), y = inOrder(b ?? []);
  return x.length === y.length && x.every((c, i) => c === y[i]);
}

/**
 * Where the picker's panel goes (fixed, in viewport pixels): under the button
 * and left-aligned with it, pulled in so it never leaves the screen (16px
 * gutters), and above the button when there is too little room below.
 */
export function regionPanelPlacement(
  trigger: { left: number; top: number; bottom: number },
  viewport: { width: number; height: number },
  gutter = 16,
): { left: number; width: number; maxHeight: number; top?: number; bottom?: number } {
  const width = Math.max(0, Math.min(380, viewport.width - gutter * 2));
  const left = Math.max(gutter, Math.min(trigger.left, viewport.width - gutter - width));
  const below = viewport.height - trigger.bottom - gutter - 6;
  const above = trigger.top - gutter - 6;
  const up = below < 300 && above > below;
  const maxHeight = Math.max(200, Math.min(460, up ? above : below));
  return up ? { left, width, maxHeight, bottom: viewport.height - trigger.top + 6 } : { left, width, maxHeight, top: trigger.bottom + 6 };
}
